import { useMutation } from '@apollo/client';
import { useRouter, useShowToast } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  JoinProSendContactMessageMutation,
  JoinProSendContactMessageMutationVariables,
  ProfessionalTypeEnum,
} from '@gql/graphql';
import { ContactReasonTypeEnum } from '@gql/graphql';
import type { JoinProPageFormValues } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';
import { useCallback } from 'react';
import { useLocation } from 'react-router';

export const JOIN_PRO_SEND_CONTACT_MESSAGE = graphql(`
  mutation JoinProSendContactMessage(
    $email: String!
    $language: String!
    $phone: String
    $lastName: String
    $messageContent: String
    $firstName: String
    $contactReasonType: ContactReasonTypeEnum!
  ) {
    contactSupport(
      messageContent: $messageContent
      email: $email
      language: $language
      phone: $phone
      lastName: $lastName
      firstName: $firstName
      contactReasonType: $contactReasonType
    )
  }
`);

const CONTACT_REASON_TYPE_PER_PRO_TYPE: Record<ProfessionalTypeEnum, ContactReasonTypeEnum> = {
  VET: ContactReasonTypeEnum.VET_REGISTRATION_REQUEST,
  GROOMER: ContactReasonTypeEnum.GROOMER_REGISTRATION_REQUEST,
  EDUCATOR: ContactReasonTypeEnum.EDUCATOR_REGISTRATION_REQUEST,
};

export const useSendContactMessage = (type: ProfessionalTypeEnum) => {
  const { language } = useRouter();
  const { translate } = useTranslation();
  const { showToast } = useShowToast();
  const { pathname } = useLocation();

  const [send, { loading, error }] = useMutation<
    JoinProSendContactMessageMutation,
    JoinProSendContactMessageMutationVariables
  >(JOIN_PRO_SEND_CONTACT_MESSAGE);

  const sendContactMessageFromForm = useCallback(
    async (values: JoinProPageFormValues) => {
      const { email, firstName, lastName, phone } = values;

      const messageContent = `Sent from: ${pathname}`;

      await send({
        variables: {
          messageContent,
          email,
          firstName,
          lastName,
          phone,
          language,
          contactReasonType: CONTACT_REASON_TYPE_PER_PRO_TYPE[type],
        },
      });

      showToast(translate('common.toasts.contactMessage'), { type: 'success' });
    },
    [pathname, send, language, type, showToast, translate],
  );

  return {
    sendContactMessageFromForm,
    sendContactMessageLoading: loading,
    sendContactMessageError: error,
  };
};
